import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { isSessionExpired } from "@/lib/session-expiration";

export interface TabletWorkSession {
  id: string;
  restaurant_id: string;
  user_id: string;
  status: 'pending' | 'active' | 'expired' | 'revoked';
  requested_at: string;
  started_at: string | null;
  expires_at: string | null;
  checkin_method: 'code' | 'approval';
  approved_by: string | null;
  device_info: Record<string, unknown> | null;
}

interface UseTabletWorkSessionReturn {
  session: TabletWorkSession | null;
  hasActiveSession: boolean;
  isLoading: boolean;
  isPending: boolean;
  remainingMinutes: number | null;
  checkinWithCode: (code: string) => Promise<boolean>;
  requestApproval: () => Promise<boolean>;
  cancelPendingRequest: () => Promise<boolean>;
  refetch: () => Promise<void>;
}

/**
 * Hook específico para sessões de trabalho no contexto do Tablet.
 * Diferente do useWaiterWorkSession, este hook recebe o restaurantId como parâmetro
 * ao invés de usar o RestaurantContext, permitindo funcionar em páginas públicas.
 */
export function useTabletWorkSession(restaurantId: string | undefined): UseTabletWorkSessionReturn {
  const { user, signOut } = useAuth();
  const [session, setSession] = useState<TabletWorkSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasLoggedOutRef = useRef(false);

  const getDeviceInfo = useCallback(() => {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      timestamp: new Date().toISOString(),
      sessionType: "tablet",
    };
  }, []);

  const fetchSession = useCallback(async () => {
    if (!user || !restaurantId) {
      setSession(null);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("waiter_work_sessions")
        .select("*")
        .eq("user_id", user.id)
        .eq("restaurant_id", restaurantId)
        .in("status", ["active", "pending"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      // Verificar se sessão ativa expirou
      if (data && data.status === "active" && data.expires_at) {
        const expiresAt = new Date(data.expires_at);
        if (expiresAt < new Date()) {
          // Sessão expirada, atualizar status
          await supabase
            .from("waiter_work_sessions")
            .update({ status: "expired", updated_at: new Date().toISOString() })
            .eq("id", data.id);
          setSession(null);
          
          // Fazer logout para liberar o dispositivo
          if (!hasLoggedOutRef.current) {
            hasLoggedOutRef.current = true;
            toast.info("Sessão expirada. Por favor, faça login novamente.");
            setTimeout(async () => {
              await signOut();
            }, 0);
          }
        } else {
          setSession(data as TabletWorkSession);
        }
      } else {
        setSession(data as TabletWorkSession | null);
      }
    } catch (error) {
      console.error("Erro ao buscar sessão:", error);
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  }, [user, restaurantId, signOut]);

  // Buscar sessão inicial
  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Escutar mudanças em tempo real
  useEffect(() => {
    if (!user || !restaurantId) return;

    const channel = supabase
      .channel(`tablet-session-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "waiter_work_sessions",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newData = payload.new as TabletWorkSession;
          
          if (payload.eventType === "UPDATE") {
            if (newData.status === "active" && session?.status === "pending") {
              toast.success("Sessão aprovada! Tablet pronto para uso.");
            } else if (newData.status === "revoked" || newData.status === "expired") {
              const message = newData.status === "revoked" 
                ? "Sua sessão foi revogada pelo gerente." 
                : "Sua sessão expirou.";
              toast.error(message);
              
              // Fazer logout automático para liberar o dispositivo
              if (!hasLoggedOutRef.current) {
                hasLoggedOutRef.current = true;
                setTimeout(async () => {
                  await signOut();
                }, 1500);
              }
              return;
            }
          }
          
          fetchSession();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, restaurantId, session?.status, fetchSession, signOut]);

  // Periodic expiration check (every 60s)
  useEffect(() => {
    if (!session || session.status !== "active") return;

    const checkExpiration = async () => {
      if (isSessionExpired(session.expires_at, session.started_at)) {
        console.log("[TabletWorkSession] Session expired by periodic check");
        try {
          await supabase
            .from("waiter_work_sessions")
            .update({ status: "expired", updated_at: new Date().toISOString() })
            .eq("id", session.id);
        } catch (e) {
          console.error("Failed to update expired session:", e);
        }
        setSession(null);
        if (!hasLoggedOutRef.current) {
          hasLoggedOutRef.current = true;
          toast.info("Sessão expirada. Por favor, faça login novamente.");
          setTimeout(async () => { await signOut(); }, 0);
        }
      }
    };

    checkExpiration();
    const timer = setInterval(checkExpiration, 60_000);
    return () => clearInterval(timer);
  }, [session, signOut]);

  // Calcular minutos restantes
  const remainingMinutes = session?.expires_at
    ? Math.max(0, Math.floor((new Date(session.expires_at).getTime() - Date.now()) / 60000))
    : null;

  const checkinWithCode = async (code: string): Promise<boolean> => {
    if (!restaurantId) {
      toast.error("Restaurante não identificado");
      return false;
    }

    try {
      const { data, error } = await supabase.rpc("waiter_checkin_with_code", {
        p_restaurant_id: restaurantId,
        p_code: code.toUpperCase(),
        p_device_info: getDeviceInfo(),
      });

      if (error) {
        if (error.message.includes("inválido")) {
          toast.error("Código inválido ou expirado");
        } else {
          toast.error("Erro ao fazer check-in");
        }
        return false;
      }

      toast.success("Check-in realizado com sucesso!");
      await fetchSession();
      return true;
    } catch (error) {
      console.error("Erro no check-in:", error);
      toast.error("Erro ao fazer check-in");
      return false;
    }
  };

  const requestApproval = async (): Promise<boolean> => {
    if (!user) {
      toast.error("Você precisa estar logado para solicitar liberação");
      return false;
    }
    
    if (!restaurantId) {
      toast.error("Restaurante não identificado");
      return false;
    }

    try {
      const { error } = await supabase.from("waiter_work_sessions").insert({
        restaurant_id: restaurantId,
        user_id: user.id,
        status: "pending",
        checkin_method: "approval",
        device_info: getDeviceInfo(),
      });

      if (error) throw error;

      toast.success("Solicitação enviada! Aguarde aprovação.");
      await fetchSession();
      return true;
    } catch (error) {
      console.error("Erro ao solicitar aprovação:", error);
      toast.error("Erro ao enviar solicitação");
      return false;
    }
  };

  const cancelPendingRequest = async (): Promise<boolean> => {
    if (!session || session.status !== "pending") return false;

    try {
      const { error } = await supabase
        .from("waiter_work_sessions")
        .update({ status: "expired", updated_at: new Date().toISOString() })
        .eq("id", session.id);

      if (error) throw error;

      toast.success("Solicitação cancelada");
      await fetchSession();
      return true;
    } catch (error) {
      console.error("Erro ao cancelar solicitação:", error);
      toast.error("Erro ao cancelar");
      return false;
    }
  };

  return {
    session,
    hasActiveSession: session?.status === "active",
    isLoading,
    isPending: session?.status === "pending",
    remainingMinutes,
    checkinWithCode,
    requestApproval,
    cancelPendingRequest,
    refetch: fetchSession,
  };
}
